import React, { useRef, useState, useEffect } from 'react';
import { motion, Variants } from 'framer-motion';
import LaptopCover from '@/assets/images/laptopCover.png';
import ReturnsIcon from '@/assets/features/returnsActive.png';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import CoverOne from "@/assets/images/returnsCover1.png";
import CoverTwo from "@/assets/images/returnsCover2.png";
import CoverThree from "@/assets/images/returnsCover3.png";

gsap.registerPlugin(ScrollTrigger);

const sectionVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.6, ease: 'easeOut' },
  }),
};

const ReturnsProcess: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [showGradient, setShowGradient] = useState(false);

  const features = [
    {
      title: 'A better approach to returns and exchanges.',
      description:
        'Give your customers the power to initiate returns and exchanges from a single, streamlined user platform. Your unique returns and exchanges flow is based on loyalty member status, lifetime value, and consumer risk.',
      sub:
        'Best of all, Protega’s automated processes free up your support team to focus on other tasks, saving your business money.',
    },
    {
      title: 'Personalize and uplevel your returns policy.',
      description: 'Drive loyalty and lower costs with an efficient returns and exchanges process that’s driven by AI technology.',
      sub:
        'Set custom refund triggers based on risk level, assign unique reimbursement options — like a refund or a store credit — based on CLTV, customize your refund window based on loyalty status, and more.',
    },
    {
      title: 'Reduce your rate of fraudulent returns with AI.',
      description:
        'Let AI-powered fraud mitigation ensure that each return or exchange is legitimate, reducing the need for manual processing.',
      sub:
        'Identify and stop fake tracking ID (FTID) fraud, minimize cost of bracketing, stop repeat abusers, and more.',
    },
  ];

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const progress = Math.min(
        1,
        Math.max(0, (window.innerHeight - rect.top) / (rect.height - window.innerHeight))
      );
      setShowGradient(progress > 0.95);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // image parallax on scroll (desktop only)
  useEffect(() => {
    if (!imageRef.current || !sectionRef.current) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sectionRef.current,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 0.5,
      }
    });

    tl.fromTo(
      imageRef.current,
      { y: 150, scale: 1 },
      { y: -150, scale: 1.3, ease: 'none' }
    );

    return () => {
      tl.scrollTrigger?.kill();
      tl.kill();
    };
  }, []);

  return (
    <section ref={sectionRef} className="tw-relative tw-w-full tw-overflow-hidden">
      {/* Black background */}
      <div className="tw-absolute tw-inset-0 tw-bg-black" />

      {/* Gradient overlay */}
      <div
        className={`
          tw-absolute tw-inset-x-6 tw-top-0 tw-bottom-0
          tw-bg-gradient-to-b tw-from-black tw-via-black tw-to-[#D357FF]
          tw-transition-opacity tw-duration-700 tw-rounded-b-2xl
          ${showGradient ? 'tw-opacity-100' : 'tw-opacity-0'}
        `}
      />

      <div className="tw-relative tw-z-10 tw-text-white">
        <div className="tw-container tw-mx-auto tw-px-6 tw-py-20 tw-space-y-32">
          {/* Feature list */}
          {features.map((f, i) => {
            const isEven = i % 2 === 1;
            const images = [CoverOne, CoverTwo, CoverThree];

            return (
              <motion.div
                key={i}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={sectionVariants}
                className={`
        tw-flex tw-flex-col md:tw-flex-row 
        ${isEven ? 'md:tw-flex-row-reverse' : ''}
        tw-items-center tw-gap-10 tw-px-5
      `}
              >
                <div className="tw-w-full md:tw-w-1/2 tw-flex tw-justify-center">
                  <img
                    src={images[i]}
                    alt={`Claim Feature ${i + 1}`}
                    className="tw-w-3/4 md:tw-w-2/3 tw-aspect-square tw-object-cover tw-rounded-xl"
                  />
                </div>
                <div className="tw-w-full md:tw-w-1/2 tw-space-y-3">
                  <h3 className="tw-text-2xl md:tw-text-3xl lg:tw-text-4xl tw-font-bold tw-leading-tight">
                    {f.title}
                  </h3>
                  <p className="tw-text-gray-300 tw-text-base md:tw-text-lg tw-leading-relaxed">
                    {f.description}
                  </p>
                  <p className="tw-text-gray-500 tw-text-sm md:tw-text-base">
                    {f.sub}
                  </p>
                </div>
              </motion.div>
            );
          })}

          {/* CTA Section */}
          <div className="tw-relative tw-overflow-visible tw-pt-20 tw-px-6">
            <div className="tw-rounded-2xl tw-p-10 lg:tw-p-16 tw-relative tw-z-10 tw-text-black">
              <p className="tw-uppercase tw-text-sm tw-text-black/60 tw-mb-1">
                Merchant Portal
              </p>
              <h2 className="tw-text-3xl md:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-leading-tight tw-max-w-2xl">
                Everything you need to fight fraud and increase loyalty.
                <br />
                <span className="tw-text-[#D357FF]/90">All under a single login.</span>
              </h2>

              <p className="tw-mt-6 tw-text-base md:tw-text-lg tw-font-semibold tw-max-w-sm tw-text-black/70">
                Your Protega Merchant Portal houses all the information you need to improve the post-purchase experience.
              </p>

              <div className="tw-flex tw-items-center tw-my-6">
                <img src={ReturnsIcon} alt="Protega Logo" className="tw-h-8" />
                <div className="tw-flex tw-flex-col tw-ml-2">
                  <div className="tw-flex tw-items-center tw-text-xl tw-font-medium tw-text-black tw-gap-0.5">
                    <span>Returns</span>
                  </div>
                  <span className="tw-text-sm tw-font-normal tw-text-black">Seamless returns that build loyalty</span>
                </div>
              </div>

              <h3 className="tw-mt-6 tw-text-2xl md:tw-text-6xl tw-max-w-2xl tw-leading-tight tw-font-bold">
                It’s the post-purchase platform you’ve been looking for.
              </h3>

              <Link
                to="/schedule-demo"
                className="tw-inline-block tw-mt-6 tw-w-full tw-text-center sm:tw-w-auto tw-font-semibold tw-py-3 tw-px-6 tw-rounded-full tw-bg-[#111111] tw-text-white hover:tw-bg-white hover:tw-text-black tw-transition-all tw-duration-700"
              >
                Schedule a Demo
              </Link>
            </div>

            {/* Laptop image */}
            <div className="tw-hidden xl:tw-block tw-absolute -tw-right-16 tw-bottom-0 tw-translate-y-12 tw-rotate-[-10deg] tw-z-0 tw-w-[700px]">
              <img
                ref={imageRef}
                src={LaptopCover}
                alt="Laptop"
                className="tw-w-full tw-object-contain tw-drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReturnsProcess;
